import { yupResolver } from "@hookform/resolvers/yup";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { env } from "configs/EnvironmentConfig";
import { formValidate } from "helpers/form_helper";
import { useCreateLocation } from "hooks";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { Button, Col, Form, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import { getPhoneValidation } from "constants/validate";

const LocationCustomerForm = props => {
  const { t } = useTranslation(["order"]);
  const { provinces, districts, wards } = props;

  const [provinceMenu, setProvinceMenu] = useState([]);
  const [districtMenu, setDistrictMenu] = useState([]);

  const initDefaultValues = {
    address: "",
    district_code: undefined,
    province_code: undefined,
    customer_id: props?.customer_id,
    type: null,
    note: null,
    contact_phone: "",
    contact_name: "",
  };

  const addressValidationSchema = yup.object().shape({
    contact_name: yup
      .string()
      .required(
        t("form.validate.required", { title: t("form.receiver_contact_name") })
      ),
    contact_phone: yup
      .string()
      .nullable()
      .required(
        t("form.validate.required", { title: t("form.receiver_contact_phone") })
      )
      .matches(getPhoneValidation(t).regex, getPhoneValidation(t).errorMessage)
      .when(getPhoneValidation(t).when),
    address: yup
      .string()
      .required(
        t("form.validate.required", { title: t("form.receiver_address") })
      ),
    district_code: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: "Phường/Xã" }))
      .nullable(),
    province_code: yup
      .object()
      .nullable()
      .required(
        t("form.validate.select", { title: t("form.receiver_province_code") })
      )
      .nullable(),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting, isDirty },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = useForm({
    resolver: yupResolver(addressValidationSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultValues,
  });

  const onSuccessCreateLocation = _data => {
    props?.handleCancel?.();
  };
  const { mutate: createLocation } = useCreateLocation(onSuccessCreateLocation);

  const handleCreateLocation = () => {
    const data = getValues();
    const location = {
      address: data?.address,
      district_code: data?.district_code?.value, // Keep district_code field for backend
      province_code: data?.province_code?.value,
      customer_id: data?.customer_id,
      type: null,
      note: null,
      contact_phone: data?.contact_phone,
      contact_name: data?.contact_name,
    };
    createLocation(location);
  };

  const handleChangeProvince = item => {
    setValue("province_code", item);
    setValue("district_code", null);
    // Call API to get districts (which are actually wards in 2-level address)
    props?.onChangeProvince?.(item?.value);
  };

  const handleChangeDistrict = item => {
    setValue("district_code", item);
  };

  useEffect(() => {
    if (provinces) {
      const provincesValue = Object.keys(provinces);
      let provinceMenuList = provincesValue.map(item => {
        return { label: provinces?.[item], value: item };
      });

      if (props.isHCMOrder === undefined) {
        setValue("province_code", undefined);
      } else {
        const provinceHCMFind = provinceMenuList.find(
          province => province.value === env.PROVINCE_CODE_DEFAULT
        );
        if (provinceHCMFind) {
          setValue("province_code", provinceHCMFind);
        }
        props?.onChangeProvince?.(env.PROVINCE_CODE_DEFAULT);
      }

      setProvinceMenu(provinceMenuList);
    }
  }, [provinces]);

  useEffect(() => {
    if (districts) {
      const districtsValue = Object.keys(districts);
      setDistrictMenu(
        districtsValue.map(item => {
          return { label: districts?.[item], value: item };
        })
      );
    }
  }, [districts]);

  return (
    <Form className="p-4">
      <Row xs={1} md={2}>
        <Col>
          <FormGroup>
            <Label htmlFor="receiver_province_code">
              {t("form.receiver_province_code")}{" "}
              <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="province_code"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("form.receiver_province_code"),
                  })}
                  options={provinceMenu}
                  isSearchable
                  className="basic-single w-100"
                  onChange={handleChangeProvince}
                  isDisabled={props.isHCMOrder}
                />
              )}
            />
            {errors.province_code && (
              <MessageFormValidate error={errors.province_code.message} />
            )}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Label htmlFor="receiver_district_code">
              Phường/Xã <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="district_code"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: "Phường/Xã",
                  })}
                  options={districtMenu}
                  isSearchable
                  isDisabled={!getValues("province_code")}
                  className="basic-single w-100"
                  onChange={handleChangeDistrict}
                />
              )}
            />
            {errors.district_code && (
              <MessageFormValidate error={errors.district_code.message} />
            )}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Label htmlFor="address">
              {t("form.receiver_address")}{" "}
              <span className="text-danger">*</span>
            </Label>
            <input
              name="address"
              type="text"
              className={`form-control ${formValidate(
                errors,
                touchedFields,
                "address"
              )}`}
              placeholder={t("placeholder.enter", {
                title: t("form.receiver_address"),
              })}
              {...register("address")}
            />
            {errors.address && (
              <MessageFormValidate error={errors.address.message} />
            )}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Label htmlFor="receiver_contact_name">
              {t("form.receiver_contact_name")}
            </Label>
            <input
              type="text"
              id="receiver_contact_name"
              placeholder={t("placeholder.enter", {
                title: t("form.receiver_contact_name"),
              })}
              className={`form-control ${formValidate(
                errors,
                touchedFields,
                "contact_name"
              )}`}
              {...register("contact_name")}
            />
            {errors.contact_name && (
              <MessageFormValidate error={errors.contact_name.message} />
            )}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Label htmlFor="receiver_contact_phone">
              {t("form.receiver_contact_phone")}
            </Label>
            <input
              type="number"
              id="receiver_contact_phone"
              placeholder={t("placeholder.enter", {
                title: t("form.receiver_contact_phone"),
              })}
              className={`form-control ${formValidate(
                errors,
                touchedFields,
                "contact_phone"
              )}`}
              {...register("contact_phone")}
            />
            {errors.contact_phone && (
              <MessageFormValidate error={errors.contact_phone.message} />
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            color="danger"
            outline
            className="border-0"
            size="xs"
            onClick={props?.handleCancel}
          >
            {t("button.cancel")}
          </Button>
        </Col>
        <Col className="text-end">
          <Button
            size="xs"
            color="primary"
            style={{ backgroundColor: "#3C64B1" }}
            onClick={() => handleSubmit(handleCreateLocation)()}
          >
            {t("button.add")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

LocationCustomerForm.propTypes = {
  isHCMOrder: PropTypes.bool,
  handleCancel: PropTypes.func,
  provinces: PropTypes.object,
  districts: PropTypes.object,
  wards: PropTypes.object,
  customer_id: PropTypes.number,
  onChangeProvince: PropTypes.func,
  onChangeDistrict: PropTypes.func,
};

export default LocationCustomerForm;
